import { useEffect, useState } from "react";
import { BellRinging, DeviceMobile, ArrowClockwise } from "@phosphor-icons/react";
import axios from "../../utils/axiosInstance";

const API = window.location.origin;

/** Formats an ISO string as "dd.MM HH:mm" in local time, or "—". */
function fmtWhen(iso) {
  if (!iso) return "—";
  const d = new Date(iso);
  if (isNaN(d.getTime())) return "—";
  return d.toLocaleString("pl-PL", {
    day: "2-digit", month: "2-digit", year: "2-digit",
    hour: "2-digit", minute: "2-digit",
  });
}

/** Shorten a user-agent string to the OS/browser gist. */
function shortUA(ua) {
  if (!ua) return "";
  if (/iPhone/i.test(ua)) return "iPhone";
  if (/iPad/i.test(ua)) return "iPad";
  if (/Android/i.test(ua)) return "Android";
  if (/Windows/i.test(ua)) return "Windows";
  if (/Macintosh|Mac OS/i.test(ua)) return "Mac";
  if (/Linux/i.test(ua)) return "Linux";
  return ua.slice(0, 30);
}

/**
 * Admin-only diagnostic card: who has enabled push notifications and on
 * how many devices. Hits /api/push/subscriptions/admin.
 */
export default function AdminPushSubscribersCard() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  const load = async () => {
    setLoading(true);
    setError(null);
    try {
      const { data: res } = await axios.get(`${API}/api/push/subscriptions/admin`);
      setData(res);
    } catch (e) {
      setError(e?.response?.data?.detail || "Nie udało się pobrać listy");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => { load(); }, []);

  const users = data?.users || [];

  return (
    <div
      className="bg-white rounded-xl border border-slate-200 shadow-sm p-4 sm:p-5"
      data-testid="admin-push-subscribers-card"
    >
      <div className="flex items-center justify-between mb-3">
        <div className="flex items-center gap-2">
          <div className="w-8 h-8 rounded-md bg-emerald-50 text-emerald-600 flex items-center justify-center">
            <BellRinging className="w-4 h-4" weight="fill" />
          </div>
          <div>
            <div className="text-sm font-semibold text-slate-800">Powiadomienia push</div>
            <div className="text-xs text-slate-500">
              {loading ? "Ładowanie..." : `${data?.unique_users || 0} użytkowników · ${data?.total_subscriptions || 0} urządzeń`}
            </div>
          </div>
        </div>
        <button
          onClick={load}
          disabled={loading}
          className="p-1.5 text-slate-500 hover:bg-slate-100 rounded-md disabled:opacity-50"
          title="Odśwież"
          data-testid="admin-push-refresh"
        >
          <ArrowClockwise className={`w-4 h-4 ${loading ? "animate-spin" : ""}`} />
        </button>
      </div>

      {error && (
        <div className="text-sm text-red-600 py-3">{error}</div>
      )}

      {!loading && !error && users.length === 0 && (
        <div className="text-sm text-slate-500 py-6 text-center">
          Nikt jeszcze nie włączył powiadomień push.
        </div>
      )}

      {users.length > 0 && (
        <div className="divide-y divide-slate-100">
          {users.map((u) => (
            <div
              key={u.user_id}
              className="py-2 flex items-start justify-between gap-3"
              data-testid={`push-subscriber-${u.user_id}`}
            >
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium text-slate-800 truncate">
                  {u.user_name || u.email || u.login || "Nieznany"}
                  <span className="ml-2 text-[10px] uppercase tracking-wide font-semibold px-1.5 py-0.5 rounded bg-slate-100 text-slate-600">
                    {u.role || "?"}
                  </span>
                </div>
                <div className="text-xs text-slate-500 mt-0.5 flex items-center gap-1 flex-wrap">
                  <DeviceMobile className="w-3 h-3" />
                  {u.devices.length} {u.devices.length === 1 ? "urządzenie" : "urządzenia"}
                  {u.devices.slice(0, 3).map((d, i) => (
                    <span key={i} className="ml-1 px-1.5 py-0.5 bg-slate-100 rounded text-[10px]">
                      {shortUA(d.user_agent)}
                    </span>
                  ))}
                </div>
              </div>
              <div className="text-xs text-slate-500 text-right shrink-0">
                <div className="whitespace-nowrap">{fmtWhen(u.last_activity)}</div>
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
